import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../../auth/AuthContext";
import PrimaryButton from "../../PrimaryButton";
import Footer from "../home/Footer";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  ButtonBase,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";
import Media_one from "../../assets/media/media_one.jpg";
// import Media_two from "../../assets/media/media_two.jpg";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import DetailsIcon from "@mui/icons-material/Details";
import ShareIcon from "@mui/icons-material/Share";
import CommentIcon from "@mui/icons-material/Comment";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";

const Media = () => {
  const [blogs, setBlogs] = useState([]);
  const [news, setNews] = useState([]);
  const [adds, setAdds] = useState([]);
  const [likes, setLikes] = useState([]);
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaToDisplay, setMediaToDisplay] = useState([]);
  const [filteredMedia, setFilteredMedia] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("news");
  const [error, setError] = useState(null);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  // Share functionality states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // Likes functionality states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [currentLikeItem, setCurrentLikeItem] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [deviceAddress, setDeviceAddress] = useState("");
  // Comments functionality states
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [currentCommentItem, setCurrentCommentItem] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [userProfileImage, setUserProfileImage] = useState("");
  const [commentEmailDialogOpen, setCommentEmailDialogOpen] = useState(false);
  const [commentUserEmail, setCommentUserEmail] = useState("");

  const authValue = useAuth();
  const { getData, addData, deleteData } = authValue;

  // Refs for animation control
  const adsContainerRef = useRef(null);
  const animationRef = useRef(null);
  const isPausedRef = useRef(false);

  // Filter ads to only show Portraite type
  const portraitAds = useMemo(() => {
    return adds.filter((ad) => ad.type === "Portraite");
  }, [adds]);

  // Get device address (simulated MAC address)
  useEffect(() => {
    const getDeviceAddress = async () => {
      try {
        // Generate a unique device identifier
        let deviceId = localStorage.getItem("device_address");
        if (!deviceId) {
          // Create a unique identifier for the device
          deviceId =
            "device_" +
            Math.random().toString(36).substr(2, 9) +
            "_" +
            Date.now().toString(36);
          localStorage.setItem("device_address", deviceId);
        }
        setDeviceAddress(deviceId);

        // Get user profile image from localStorage if exists
        const savedImage = localStorage.getItem("user_profile_image");
        if (savedImage) {
          setUserProfileImage(savedImage);
        }

        // Get user email from localStorage if exists
        const savedEmail = localStorage.getItem("user_email");
        if (savedEmail) {
          setUserEmail(savedEmail);
          setCommentUserEmail(savedEmail);
        }
      } catch (error) {
        console.error("Error getting device address:", error);
        // Fallback device address
        const fallbackAddress =
          "fallback_device_" + Math.random().toString(36).substr(2, 9);
        setDeviceAddress(fallbackAddress);
      }
    };

    getDeviceAddress();
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch services and department in parallel
        const [
          blogsResponse,
          newsResponse,
          addsResponse,
          authorResponse,
          likesResponse,
          commentsResponse,
        ] = await Promise.all([
          getData("proccess.php", "blog"),
          getData("proccess.php", "news_V2"),
          getData("proccess.php", "adverts_V2"),
          getData("proccess.php", "staffs"),
          getData("proccess.php", "likes_V2"),
          getData("proccess.php", "comments_V2"),
        ]);

        // Safe data setting with fallbacks
        setBlogs(blogsResponse?.data || []);
        setNews(newsResponse?.data || []);
        setAdds(addsResponse?.data || []);
        setAuthor(authorResponse?.data || []);
        setComments(commentsResponse?.data || []);
        setLikes(likesResponse?.data || []);

        // Set errors if responses indicate failure
        if (!blogsResponse?.success) {
          setError(
            (prev) => prev || blogsResponse?.message || "Failed to fetch blogs"
          );
        }
        if (!newsResponse?.success) {
          setError(
            (prev) => prev || newsResponse?.message || "Failed to fetch news"
          );
        }
        if (!addsResponse?.success) {
          setError(
            (prev) => prev || addsResponse?.message || "Failed to fetch ads"
          );
        }
        if (!authorResponse?.success) {
          setError(
            (prev) =>
              prev || authorResponse?.message || "Failed to fetch authors"
          );
        }
      } catch (err) {
        console.error("Fetch error in Media:", err);
        setError(err.message || "Network error");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [getData]);

  useEffect(() => {
    if (selectedCategory === "blogs") {
      setMediaToDisplay(blogs || []);
    } else if (selectedCategory === "news") {
      setMediaToDisplay(news || []);
    }
  }, [selectedCategory, blogs, news]);

  // Filter media based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredMedia(mediaToDisplay);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = mediaToDisplay.filter((item) => {
      const title = item.title?.toLowerCase() || "";
      const content =
        selectedCategory === "blogs"
          ? item.story?.toLowerCase()
          : item.description?.toLowerCase() || "";

      return title.includes(query) || content.includes(query);
    });

    setFilteredMedia(filtered);
  }, [searchQuery, mediaToDisplay, selectedCategory]);

  // Auto-scroll ads animation
  useEffect(() => {
    if (!portraitAds || portraitAds.length === 0) return;

    const startAnimation = () => {
      if (isPausedRef.current) return;

      animationRef.current = requestAnimationFrame(() => {
        setCurrentAdIndex((prevIndex) => {
          // Reset to 0 if we've reached the end, otherwise increment
          return prevIndex >= portraitAds.length - 1 ? 0 : prevIndex + 1;
        });
      });
    };

    // Start the interval for auto-scrolling (change ad every 5 seconds)
    const intervalId = setInterval(startAnimation, 5000);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [portraitAds]);

  const getAuthor = (authorId) => {
    const authorObj = author.find((auth) => auth.Id === authorId);
    return authorObj ? authorObj : "Unknown Author";
  };

  // Check if user has already liked a specific content
  const hasUserLiked = (contentType, contentId) => {
    return likes.some(
      (like) =>
        like.content_type === contentType &&
        like.content_id == contentId &&
        like.device_address === deviceAddress
    );
  };

  // Get like count for specific content
  const getLikeCount = (contentType, contentId) => {
    const contentLikes = likes.filter(
      (like) =>
        like.content_type === contentType && like.content_id == contentId
    );

    // Sum up all the number_of_likes
    return contentLikes.reduce(
      (total, like) => total + (parseInt(like.number_of_likes) || 0),
      0
    );
  };

  // Get comments for specific content
  const getContentComments = (contentType, contentId) => {
    return comments
      .filter(
        (comment) =>
          comment.content_type === contentType &&
          comment.content_id == contentId &&
          comment.satus === "active" // Only show approved comments
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by latest first
  };

  // Get comment count for specific content
  const getCommentCount = (contentType, contentId) => {
    return getContentComments(contentType, contentId).length;
  };

  // Handle like functionality
  const handleLike = async (item) => {
    if (!deviceAddress) {
      setSnackbarMessage("Device not recognized. Please refresh the page.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const contentType = selectedCategory === "blogs" ? "blog" : "news";
    const contentId = item.id;

    // Check if user has already liked this content
    const existingLike = likes.find(
      (like) =>
        like.content_type === contentType &&
        like.content_id == contentId &&
        like.device_address === deviceAddress
    );

    if (existingLike) {
      // Unlike - decrement likes
      await handleUnlike(existingLike);
    } else {
      // New like - check if we have user email from previous likes
      const userPreviousLike = likes.find(
        (like) => like.device_address === deviceAddress
      );

      if (userPreviousLike && userPreviousLike.email) {
        // Use existing email
        await submitLike(contentType, contentId, userPreviousLike.email, 1);
      } else if (userEmail) {
        // Use stored email
        await submitLike(contentType, contentId, userEmail, 1);
      } else {
        // Ask for email
        setCurrentLikeItem(item);
        setEmailDialogOpen(true);
      }
    }
  };

  // Handle unlike functionality
  const handleUnlike = async (existingLike) => {
    try {
      const response = await deleteData(
        "proccess.php",
        "likes_V2",
        existingLike.id
      );

      if (response?.success) {
        // Update local state - remove the like completely
        setLikes((prevLikes) =>
          prevLikes.filter((like) => like.id !== existingLike.id)
        );

        setSnackbarMessage("Like removed!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        throw new Error(response?.message || "Failed to remove like");
      }
    } catch (error) {
      console.error("Error unliking:", error);
      setSnackbarMessage("Failed to remove like. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Submit like to server
  const submitLike = async (
    contentType,
    contentId,
    email,
    numberOfLikes = 1
  ) => {
    try {
      const response = await addData("proccess.php", "likes_V2", {
        content_type: contentType,
        content_id: contentId,
        device_address: deviceAddress,
        email: email,
        number_of_likes: numberOfLikes,
      });

      if (response?.success) {
        // Add new like to local state
        if (response.data) {
          setLikes(response.data);
        }

        // Save email for future use
        localStorage.setItem("user_email", email);
        setUserEmail(email);

        setSnackbarMessage("Liked successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmailDialogOpen(false);
      } else {
        throw new Error(response?.message || "Failed to like");
      }
    } catch (error) {
      console.error("Error liking:", error);
      setSnackbarMessage("Failed to like. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle email submission for likes
  const handleEmailSubmit = () => {
    if (!userEmail.trim()) {
      setSnackbarMessage("Please enter your email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (currentLikeItem) {
      const contentType = selectedCategory === "blogs" ? "blog" : "news";
      submitLike(contentType, currentLikeItem.id, userEmail, 1);
    }
  };

  // Handle email dialog close
  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setCurrentLikeItem(null);
  };

  // Handle comments functionality
  const handleCommentsClick = (item) => {
    setCurrentCommentItem(item);
    setCommentsDialogOpen(true);
  };

  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!commentText.trim()) {
      setSnackbarMessage("Please enter a comment.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!deviceAddress) {
      setSnackbarMessage("Device not recognized. Please refresh the page.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Check if we have user email
    let emailToUse = commentUserEmail;

    if (!emailToUse) {
      // Check if we have email from likes
      const userPreviousLike = likes.find(
        (like) => like.device_address === deviceAddress
      );
      if (userPreviousLike && userPreviousLike.email) {
        emailToUse = userPreviousLike.email;
      } else if (userEmail) {
        emailToUse = userEmail;
      } else {
        // Ask for email
        setCommentEmailDialogOpen(true);
        return;
      }
    }

    await submitComment(emailToUse);
  };

  // Submit comment to server
  const submitComment = async (email) => {
    try {
      const contentType = selectedCategory === "blogs" ? "blog" : "news";
      const contentId = currentCommentItem.id;

      const response = await addData("proccess.php", "comments_V2", {
        content_type: contentType,
        content_id: contentId,
        device_address: deviceAddress,
        email: email,
        comment: commentText.trim(),
        image: userProfileImage || "", // Store user's profile image if available
        satus: "pending", // Comments start as pending for approval
      });

      if (response?.success) {
        // Add new comment to local state - add it to the beginning of the array
        if (response.data) {
          setComments((prevComments) => [response.data, ...prevComments]);
        }

        // Save email for future use
        localStorage.setItem("user_email", email);
        setCommentUserEmail(email);
        setUserEmail(email);

        setSnackbarMessage("Comment submitted for approval!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setCommentText("");
        setCommentEmailDialogOpen(false);
      } else {
        throw new Error(response?.message || "Failed to submit comment");
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      setSnackbarMessage("Failed to submit comment. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handle comment email submission
  const handleCommentEmailSubmit = () => {
    if (!commentUserEmail.trim()) {
      setSnackbarMessage("Please enter your email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(commentUserEmail)) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    submitComment(commentUserEmail);
  };

  // Handle comments dialog close
  const handleCommentsDialogClose = () => {
    setCommentsDialogOpen(false);
    setCurrentCommentItem(null);
    setCommentText("");
  };

  // Handle comment email dialog close
  const handleCommentEmailDialogClose = () => {
    setCommentEmailDialogOpen(false);
    setCommentUserEmail("");
  };

  // Format date for comments
  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Handle mouse enter/leave for pause on hover
  const handleAdsMouseEnter = () => {
    isPausedRef.current = true;
  };

  const handleAdsMouseLeave = () => {
    isPausedRef.current = false;
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Share functionality - Copy media link to clipboard
  const handleShare = async (item) => {
    try {
      // Construct the share URL based on the category
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/media/${
        selectedCategory === "blogs" ? "blog" : "news"
      }?uuid=${item.id}&uucat=${selectedCategory}`;

      // Use the Clipboard API to copy the URL
      await navigator.clipboard.writeText(shareUrl);

      // Show success message
      setSnackbarMessage("Link copied to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy link: ", err);

      // Fallback for browsers that don't support Clipboard API
      try {
        // Create a temporary input element
        const tempInput = document.createElement("input");
        const shareUrl = `${window.location.origin}/media/${
          selectedCategory === "blogs" ? "blog" : "news"
        }?uuid=${item.id}&uucat=${selectedCategory}`;
        tempInput.value = shareUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);

        setSnackbarMessage("Link copied to clipboard!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (fallbackErr) {
        console.error("Fallback copy failed: ", fallbackErr);
        setSnackbarMessage(
          "Failed to copy link. Please copy the URL manually."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  // Enhanced share functionality with native share dialog
  const handleNativeShare = async (item) => {
    if (navigator.share) {
      try {
        const shareUrl = `${window.location.origin}/media/${
          selectedCategory === "blogs" ? "blog" : "news"
        }?uuid=${item.id}&uucat=${selectedCategory}`;
        await navigator.share({
          title: item.title || "Check this out!",
          text: item.story || item.description || "",
          url: shareUrl,
        });
      } catch (err) {
        // User canceled the share or something went wrong
        console.log("Share was canceled or failed:", err);
      }
    } else {
      // Fallback to copy to clipboard
      handleShare(item);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Get the media items to display (filtered or all)
  const displayMedia = searchQuery ? filteredMedia : mediaToDisplay;

  // Safe rendering function for media items
  const renderMediaItem = (item, index) => {
    if (!item) return null;

    const title = item.title || "Untitled";
    const author =
      selectedCategory === "blogs" ? getAuthor(item.postedBy) : null;
    const date = selectedCategory === "blogs" ? item.date : item.dateCreated;
    const content =
      selectedCategory === "blogs" ? item.story : item.description;

    const formattedDate = date
      ? new Date(date).toLocaleDateString()
      : "No date";
    const truncatedContent =
      content && content.length > 200
        ? content.substring(0, 200) + "..."
        : content || "No content available";

    const contentType = selectedCategory === "blogs" ? "blog" : "news";
    const hasLiked = hasUserLiked(contentType, item.id);
    const likeCount = getLikeCount(contentType, item.id);
    const commentCount = getCommentCount(contentType, item.id);

    return (
      <Box
        key={item.id || index}
        sx={{
          flex: 1,
          minWidth: "250px",
          maxWidth: "600px",
          backgroundColor: "#f8f8f8ff",
          borderBottom: "1px solid #ccc",
          borderRadius: ".5rem",
          marginBottom: "1rem",
        }}
        boxShadow={2}
      >
        {selectedCategory === "blogs" ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1rem",
              }}
            >
              <img
                src={`https://mindsinaction.com.na/assets/media/pictures/${author.image}`}
                alt={author.FirstName}
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "cover",
                  borderRadius: "50%",
                  backgroundColor: "#32a059",
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  color="#32a059"
                  fontWeight={700}
                  textTransform={"capitalize"}
                >
                  {author.FirstName} {author.LastName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formattedDate}
                </Typography>
              </Box>
            </Box>
            <Box
              style={{
                width: "100%",
                height: "auto",
                overflow: "hidden",
                maxHeight: "400px",
              }}
            >
              <img
                src={`https://mindsinaction.com.na/assets/media/pictures/${item.image}`}
                alt={title}
                style={{ width: "100%", height: "auto", objectFit: "cover" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "1rem",
                padding: ".3rem 1rem",
              }}
            >
              <Box>
                {/* Likes functionality */}
                <IconButton onClick={() => handleLike(item)}>
                  {hasLiked ? (
                    <FavoriteIcon sx={{ color: "#ff6b6b" }} />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </IconButton>
                {/* Comments functionality */}
                <IconButton onClick={() => handleCommentsClick(item)}>
                  <CommentIcon />
                </IconButton>
                {/* Share functionality */}
                <IconButton onClick={() => handleNativeShare(item)}>
                  <ShareIcon />
                </IconButton>
              </Box>

              <IconButton>
                <TurnedInNotIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                padding: "0 1rem 1rem 1rem",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {likeCount} {likeCount === 1 ? "Like" : "Likes"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {commentCount} {commentCount === 1 ? "Comment" : "Comments"}
              </Typography>
            </Box>
            <Typography
              variant="h6"
              gutterBottom
              marginBottom={"1rem"}
              textTransform={"uppercase"}
              fontWeight={700}
              color="#32a059"
              padding={"0 1rem"}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              sx={{ marginTop: "0.5rem", padding: "0 1rem" }}
              dangerouslySetInnerHTML={{
                __html: truncatedContent || "",
              }}
            />
            {content && content.length > 200 ? (
              <Box
                sx={{
                  width: "100%",
                  padding: "0 1rem",
                  textAlign: "right",
                  backgroundColor: "#e7e7e7ff",
                  marginTop: ".5rem",
                }}
              >
                <ButtonBase
                  sx={{
                    padding: ".5rem 1rem",
                    margin: ".2rem",
                    color: "#32a059",
                    textDecoration: "underline",
                  }}
                  href={`/media/blog?uuid=${item.id}&uucat=blogs`}
                >
                  {content && content.length > 200 ? "View" : "read less"}
                </ButtonBase>
              </Box>
            ) : (
              <Box sx={{ height: "1rem" }}></Box>
            )}
          </Box>
        ) : (
          <Box>
            <Typography 
              dangerouslySetInnerHTML={{ 
                __html: title?.substring(0, 80) + (title?.length > 80 ? "..." : "") || "No Title." 
              }}
              variant="h6"
              gutterBottom
              textTransform={"uppercase"}
              fontWeight={700}
              color="#32a059"
              padding={"1rem 1rem 0 1rem"}
            />
            <Typography
              variant="body"
              color="textSecondary"
              padding={"0 1rem"}
              marginBottom={"1rem"}
            >
              {formattedDate}
            </Typography>
            <Box
              style={{
                width: "100%",
                height: "auto",
                overflow: "hidden",
                maxHeight: "215px",
              }}
            >
              <img
                src={`https://mindsinaction.com.na/assets/media/pictures/${item.image}`}
                alt={title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "1rem",
                padding: ".3rem 1rem",
              }}
            >
              <Box>
                {/* Likes functionality */}
                <IconButton onClick={() => handleLike(item)}>
                  {hasLiked ? (
                    <FavoriteIcon sx={{ color: "#ff6b6b" }} />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </IconButton>
                {/* Comments functionality */}
                <IconButton onClick={() => handleCommentsClick(item)}>
                  <CommentIcon />
                </IconButton>
                {/* Share functionality */}
                <IconButton onClick={() => handleNativeShare(item)}>
                  <ShareIcon />
                </IconButton>
              </Box>
              <IconButton>
                <TurnedInNotIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                padding: "0 1rem 1rem 1rem",
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {likeCount} {likeCount === 1 ? "Like" : "Likes"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {commentCount} {commentCount === 1 ? "Comment" : "Comments"}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{ marginTop: "0.5rem", padding: "0 1rem" }}
              dangerouslySetInnerHTML={{
                __html: truncatedContent || "",
              }}
            />
            {content && content.length > 200 ? (
              <Box
                sx={{
                  width: "100%",
                  padding: "0 1rem",
                  textAlign: "right",
                  backgroundColor: "#e7e7e7ff",
                  marginTop: ".5rem",
                }}
              >
                <ButtonBase
                  sx={{
                    padding: ".5rem 1rem",
                    margin: ".2rem",
                    color: "#32a059",
                    textDecoration: "underline",
                  }}
                  href={`/media/news?uuid=${item.id}&uucat=news`}
                >
                  {content && content.length > 200 ? "View" : "read less"}
                </ButtonBase>
              </Box>
            ) : (
              <Box sx={{ height: "1rem" }}></Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Render current ad with animation
  const renderCurrentAd = () => {
    if (!portraitAds || portraitAds.length === 0) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#e0e0e0",
            borderRadius: "8px",
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No ads available
          </Typography>
        </Box>
      );
    }

    const currentAd = portraitAds[currentAdIndex];

    return (
      <Box
        ref={adsContainerRef}
        onMouseEnter={handleAdsMouseEnter}
        onMouseLeave={handleAdsMouseLeave}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {/* Animated ad content */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            animation: isPausedRef.current
              ? "none"
              : "slideInFromRight 0.8s ease-out",
            "@keyframes slideInFromRight": {
              "0%": {
                transform: "translateX(100%)",
                opacity: 0,
              },
              "100%": {
                transform: "translateX(0)",
                opacity: 1,
              },
            },
          }}
        >
          {/* Ad Image */}
          {currentAd.image && (
            <Box
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "100%",
                overflow: "hidden",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              <img
                src={`https://mindsinaction.com.na/assets/media/pictures/${currentAd.image}`}
                alt={currentAd.title || "Advertisement"}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: "4rem",
          backgroundColor: "#ffffffff",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundImage: `url(${Media_one})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#ffffffbb",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "1200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "2rem",
                marginTop: "50px",
                marginBottom: "50px",
                padding: "1rem",
                paddingTop: "4rem"
              }}
            >
              <h1 className="primary-header">our media</h1>
              {error ? (
                <p
                  style={{
                    color: "red",
                    fontSize: "1rem",
                  }}
                >
                  {error}
                </p>
              ) : (
                <h2
                  style={{
                    color: "green",
                    fontSize: "1.5rem",
                    textTransform: "uppercase",
                    fontWeight: "700",
                  }}
                >
                  ./{selectedCategory}
                </h2>
              )}
            </div>
          </div>
        </div>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "1200px",
            height: "auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "1rem",
          }}
        >
          <Box
            sx={{
              background: "#bdbfddff",
              width: "100%",
              margin: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", sm: "none", md: "block" },
              }}
            >
              <ButtonBase
                onClick={() => {
                  setSelectedCategory("news");
                  setSearchQuery(""); // Clear search when switching categories
                }}
                sx={{
                  padding: ".5rem 1rem",
                  fontSize: "1.3rem",
                  backgroundColor:
                    selectedCategory === "news" ? "#32a059" : "transparent",
                  color: selectedCategory === "news" ? "#fff" : "#000",
                  borderRadius: "0px",
                  "&:hover": {
                    backgroundColor:
                      selectedCategory === "news" ? "#2a8a4a" : "#e0e0e0",
                  },
                }}
              >
                News
              </ButtonBase>
              <ButtonBase
                onClick={() => {
                  setSelectedCategory("blogs");
                  setSearchQuery(""); // Clear search when switching categories
                }}
                sx={{
                  padding: ".5rem 1rem",
                  fontSize: "1.3rem",
                  backgroundColor:
                    selectedCategory === "blogs" ? "#32a059" : "transparent",
                  color: selectedCategory === "blogs" ? "#fff" : "#000",
                  borderRadius: "0px",
                  "&:hover": {
                    backgroundColor:
                      selectedCategory === "blogs" ? "#2a8a4a" : "#e0e0e0",
                  },
                }}
              >
                Blogs
              </ButtonBase>
            </Box>

            {/* Mobile dropdown */}
            <Box
              sx={{
                display: { xs: "block", sm: "block", md: "none" },
                padding: "0.5rem",
              }}
            >
              <FormControl variant="standard">
                <Select
                  labelId="categories-label"
                  id="categories"
                  value={selectedCategory}
                  label="Categories"
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSearchQuery(""); // Clear search when switching categories
                  }}
                  variant="standard"
                  sx={{ minWidth: "120px" }}
                >
                  <MenuItem key="blogs" value="blogs">
                    Blogs
                  </MenuItem>
                  <MenuItem key="news" value="news">
                    News
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Search */}
            <Box
              sx={{
                backgroundColor: "#fdfdfdff",
                width: "250px",
                height: "auto",
                borderRadius: "25px",
                marginRight: "1rem",
                display: "flex",
                alignItems: "center",
                padding: "0 0.5rem",
                border: "1px solid #e0e0e0",
              }}
            >
              <SearchIcon sx={{ color: "#666", marginRight: "0.5rem" }} />
              <input
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  width: "100%",
                  padding: ".5rem",
                  outline: "none",
                  fontSize: "0.9rem",
                }}
                type="text"
                placeholder={`Search ${selectedCategory} by title or content...`}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <IconButton
                  onClick={handleClearSearch}
                  sx={{ padding: "4px", color: "#666" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Search Results Info */}
        {searchQuery ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "1200px",
              padding: "0 1rem",
              height: "2.5rem",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {filteredMedia.length} result(s) found for "{searchQuery}" in{" "}
              {selectedCategory}
              {filteredMedia.length === 0 && (
                <span style={{ color: "#ff6b6b", marginLeft: "0.5rem" }}>
                  - No matches found
                </span>
              )}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: "2.5rem" }}></Box>
        )}

        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "1200px",
            height: "75vh",
            display: "flex",
            flexWrap: "wrap-reverse",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            margin: "1rem",
          }}
        >
          {/* Adds box - Only show if there are portrait ads */}
          {portraitAds.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                flex: 1,
                minWidth: "300px",
                maxWidth: "350px",
                height: "100%",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              {renderCurrentAd()}
            </Box>
          )}

          {/* Media contents box */}
          <Box
            sx={{
              flex: portraitAds.length > 0 ? 1 : "100%",
              minWidth: "250px",
              height: "75vh",
              display: "flex",
              flexWrap: 'wrap',
              gap: '1rem',
              alignItems: "start",
              justifyContent: displayMedia.length > 0 ? "start" : "center",
              overflowY: "auto",
              overflowX: "hidden",
              padding: "1rem",
            }}
          >
            {loading ? (
              <Typography>Loading...</Typography>
            ) : displayMedia.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  color: "textSecondary",
                  padding: "2rem",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {searchQuery
                    ? "No matching media found"
                    : `No ${selectedCategory} available`}
                </Typography>
                {searchQuery && (
                  <Typography variant="body2">
                    Try adjusting your search terms or browse all{" "}
                    {selectedCategory}
                  </Typography>
                )}
              </Box>
            ) : (
              displayMedia.map(renderMediaItem)
            )}
          </Box>
        </div>
      </div>

      {/* Email Dialog for Likes */}
      <Dialog open={emailDialogOpen} onClose={handleEmailDialogClose}>
        <DialogTitle>Enter Your Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter your email address to like this content.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleEmailSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailDialogClose}>Cancel</Button>
          <Button onClick={handleEmailSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialogOpen}
        onClose={handleCommentsDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Comments
          {currentCommentItem && (
            <Typography variant="body2" color="textSecondary">
              {currentCommentItem.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Comments List */}
          {currentCommentItem && (
            <List sx={{ maxHeight: 400, overflow: "auto", mb: 2 }}>
              {getContentComments(
                selectedCategory === "blogs" ? "blog" : "news",
                currentCommentItem.id
              ).map((comment, index) => (
                <React.Fragment key={comment.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar
                        src={
                          comment.image
                            ? `https://mindsinaction.com.na/assets/media/pictures/${comment.image}`
                            : ""
                        }
                        alt={comment.email}
                      >
                        {!comment.image && comment.email
                          ? comment.email.charAt(0).toUpperCase()
                          : ""}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.primary">
                          {comment.comment}
                        </Typography>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {comment.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCommentDate(comment.date)}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  {index <
                    getContentComments(
                      selectedCategory === "blogs" ? "blog" : "news",
                      currentCommentItem.id
                    ).length -
                      1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
              {getContentComments(
                selectedCategory === "blogs" ? "blog" : "news",
                currentCommentItem.id
              ).length === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  textAlign="center"
                  py={2}
                >
                  No comments yet. Be the first to comment!
                </Typography>
              )}
            </List>
          )}

          {/* Add Comment Section */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleCommentSubmit();
                }
              }}
            />
            <IconButton
              onClick={handleCommentSubmit}
              disabled={!commentText.trim()}
              color="primary"
              sx={{ mt: 0.5 }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Email Dialog for Comments */}
      <Dialog
        open={commentEmailDialogOpen}
        onClose={handleCommentEmailDialogClose}
      >
        <DialogTitle>Enter Your Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter your email address to post a comment.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={commentUserEmail}
            onChange={(e) => setCommentUserEmail(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCommentEmailSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommentEmailDialogClose}>Cancel</Button>
          <Button onClick={handleCommentEmailSubmit} variant="contained">
            Submit Comment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* footer */}
      <Footer />
    </>
  );
};

export default Media;
